import { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

const currencies = {
  MAD: { symbol: 'MAD', locale: 'fr-MA' },
  EUR: { symbol: '€', locale: 'fr-FR' },
  USD: { symbol: '$', locale: 'en-US' },
};

// Price mappings for each plan/feature
const prices = {
  // Text AI Agent
  starter: { MAD: 999, EUR: 89, USD: 99 },
  starterAnnual: { MAD: 799, EUR: 71, USD: 79 },
  pro: { MAD: 1999, EUR: 189, USD: 199 },
  proAnnual: { MAD: 1599, EUR: 151, USD: 159 },
  // Voice AI Agent
  voiceSetup: { MAD: 5000, EUR: 500, USD: 550 },
  voicePerMin: { MAD: 2.50, EUR: 0.25, USD: 0.27 },
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('MAD');

  // Auto-detect country (simplified)
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        // Try to detect based on timezone
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz.includes('Europe')) {
          setCurrency('EUR');
        } else if (tz.includes('America')) {
          setCurrency('USD');
        } else {
          setCurrency('MAD'); // Default to MAD
        }
      } catch {
        setCurrency('MAD');
      }
    };
    // Keep MAD as default regardless of detection
    setCurrency('MAD');
  }, []);

  const getPrice = (priceKey) => {
    return prices[priceKey]?.[currency] || 0;
  };

  const formatPrice = (priceKey, options = {}) => {
    const price = getPrice(priceKey);
    const { showDecimals = false, perUnit = '' } = options;
    
    if (showDecimals) {
      return `${price.toFixed(2)} ${currencies[currency].symbol}${perUnit}`;
    }
    return `${Math.round(price)} ${currencies[currency].symbol}${perUnit}`;
  };

  const getCurrencySymbol = () => currencies[currency].symbol;

  return (
    <CurrencyContext.Provider value={{ 
      currency, 
      setCurrency, 
      currencies: Object.keys(currencies),
      getPrice,
      formatPrice,
      getCurrencySymbol,
      prices
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};
